// A creature's statblock actions, from the Compass by way of the connector.
//
// The DM Screen keeps no statblock text: its monster catalogue does not carry
// it and no other state module has one. The Compass does, and already parses it
// into named actions — so rather than a new fetch, this rides the port. Every
// creature sent to Roll20 hands the connector a document whose `attribs` are
// the parsed statblock, and lmc-creature-actions.js keeps the actions for the
// session. A creature that joined the fight is a creature whose actions are
// known.
//
// The important distinction this module preserves is UNKNOWN versus NONE. A
// creature that has never been sent has no entry, which is not the same as a
// creature with no actions, and a caller that confuses them will decide a behir
// cannot swallow.
import { newRequestId, request } from "../bridge";
import { findSwallowAction, type SwallowRule } from "./swallowed";
import { fromActions, fromAttribs, type Row, type StatBlock } from "./statBlock";

export interface CreatureAction { name: string; text: string; family?: string }
interface Answer { ok: boolean; name?: string; actions?: CreatureAction[]; rows?: Row[]; known?: boolean; error?: string }

/* THE WHOLE STATBLOCK, when the connector is new enough to send it.

   The same lane, the same cache, the same creatures. The actions have always
   come back as {name, text, family}, which is already most of a printed block;
   connector 0.8.210 adds the header rows beside them (armour class, hit points,
   abilities, senses). An older connector simply does not send `rows`, and the
   reader shows the actions with no header rather than refusing -- feature
   detection on the field, not a capability flag, because no new lane exists to
   gate. */
const ROWS = new Map<string, Row[]>();

/** "Goblin 2" and "Goblin (LMC)" are the same statblock as "Goblin". */
export const bareName = (v: string): string =>
  String(v || "").replace(/\s+/g, " ").trim().toLowerCase().replace(/\s*\((?:lmc)\)$/i, "").replace(/\s+\d+$/, "");

/** Only ANSWERS are cached. An unknown creature is asked for again, because the
 *  whole point is that the cache fills as creatures join the fight. */
const CACHE = new Map<string, CreatureAction[]>();

export async function creatureActions(name: string): Promise<CreatureAction[] | null> {
  const key = bareName(name);
  if (!key) return null;
  const had = CACHE.get(key);
  if (had) return had;
  const r = await request<Answer>(
    { type: "DM_SCREEN_LMC_CREATURE_ACTIONS_REQUEST", requestId: newRequestId("creature-actions"), name },
    { resultType: "DM_SCREEN_LMC_CREATURE_ACTIONS_RESULT", timeoutMs: 12000 },
  ).catch(() => ({ ok: false } as Answer));
  if (!r.ok || !r.known || !Array.isArray(r.actions) || !r.actions.length) return null;
  if (Array.isArray(r.rows) && r.rows.length) ROWS.set(key, r.rows);
  CACHE.set(key, r.actions);
  return r.actions;
}

/** The swallow rule out of a set of actions already in hand (no lane, testable). */
export const swallowRuleFromActions = (actions: CreatureAction[] | null | undefined): SwallowRule | null =>
  (actions && actions.length ? findSwallowAction(actions)?.rule ?? null : null);

/** The swallow rule for a creature by name: null when unknown AND when it has none. */
export async function swallowRuleFor(name: string): Promise<SwallowRule | null> {
  return swallowRuleFromActions(await creatureActions(name));
}

/** A creature's statblock for reading, or null when nothing knows it.
 *
 *  Asks the same lane as creatureActions, so a creature that has joined the
 *  fight is a creature whose block can be read, and the second ask is instant. */
export async function creatureBlock(name: string): Promise<StatBlock | null> {
  const actions = await creatureActions(name);
  if (!actions) return null;
  const rows = ROWS.get(bareName(name));
  /* The header when it is there, the actions when it is not. fromAttribs reads
     the repeating rows too, so the rows path is complete on its own. */
  return rows ? fromAttribs(rows, name) : fromActions(name, actions);
}

export const __clearCreatureActionCache = () => { CACHE.clear(); ROWS.clear(); };
